'use client';

import { envHasDevFlagsEnabled } from '../util/envHasDevFlagsEnabled';
import { useIsLDClientReady } from './useIsLDClientReady';
import { CookieValueTypes, getCookie } from 'cookies-next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';

/**
 * This hook is used to get the feature flags from the cookies.
 * It uses the cookies-next library to get the cookies.
 * It returns an object with the feature flags and their values.
 * It also sets the isCookieValuesSet state to true when the cookies are set.
 */
export function useLDCookieOverride(): {
  cookieValues: Record<string, CookieValueTypes>;
  isCookieValuesSet: boolean;
} {
  const flags = useFlags();
  const flagKeys = Object.keys(flags);
  const isClientReady = useIsLDClientReady();
  const [cookieValues, setCookieValues] = useState<
    Record<string, CookieValueTypes>
  >({});
  const [isCookieValuesSet, setIsCookieValuesSet] = useState(false);

  useEffect(() => {
    if (envHasDevFlagsEnabled() && isClientReady) {
      flagKeys.forEach((flag) => {
        const cookieValue = getCookie(flag);
        if (cookieValue !== undefined) {
          setCookieValues((prevValues) => ({
            ...prevValues,
            [flag]: cookieValue,
          }));
        }
      });
      setIsCookieValuesSet(true);
    }
    // Cookie values are set once when the component mounts and when the client is ready.
    // They are not dependent on the component's state or props,
    // so we can safely ignore the exhaustive-deps rule here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClientReady]);

  return { isCookieValuesSet, cookieValues };
}
